import type { RideAttraction, RideClassification } from '../../hooks/useRideIntelligence'

const LOADING_TYPE_COLORS: Record<string, string> = {
  batch: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  continuous: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  gondola: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  sequential: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
}

const TYPE_COLORS: Record<string, string> = {
  'Adult Ride': 'text-[#0282bf]',
  'Kids Ride': 'text-[#0a9356]',
  'Game': 'text-[#0a9356]',
  'Food Stand': 'text-[#ffa726]',
  'Gate': 'text-[#CF4821]',
}

function fmtNum(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return n.toLocaleString()
}

function fmtDollars(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtPct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return `${(n * 100).toFixed(1)}%`
}

function fmtCycleTime(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return '—'
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

interface RideIntelligenceCardProps {
  attraction: RideAttraction
  classification?: RideClassification | null
  rank?: number
  'data-testid'?: string
}

export function RideIntelligenceCard({
  attraction,
  classification,
  rank,
  'data-testid': testId,
}: RideIntelligenceCardProps) {
  const a = attraction
  const c = classification
  const loadingType = c?.loading_type || a.loading_type
  const loadingBadge = loadingType ? LOADING_TYPE_COLORS[loadingType] ?? 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300' : null
  const typeColor = TYPE_COLORS[a.attraction_type] ?? 'text-slate-500'
  const ulrPct = a.total_scans > 0 ? a.ulr_scans / a.total_scans : null

  return (
    <article
      data-testid={testId ?? `ride-card-${a.attraction_id}`}
      className="rounded-card border border-hairline bg-white p-4 shadow-card-soft transition hover:shadow-card hover:-translate-y-0.5 dark:border-slate-700 dark:bg-slate-800"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-display text-base font-semibold text-ink dark:text-slate-100">
            {a.attraction_name}
          </h3>
          <span className={`text-xs font-medium ${typeColor}`}>
            {a.attraction_type}
          </span>
        </div>
        {rank != null && (
          <span className="shrink-0 rounded-full bg-butler-50 px-2 py-0.5 text-xs font-bold text-butler-700 dark:bg-butler-900/30 dark:text-butler-300">
            #{rank}
          </span>
        )}
      </div>

      {/* Loading type + cycle info */}
      {(loadingBadge || c?.avg_cycle_time_sec) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {loadingBadge && (
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${loadingBadge}`}>
              {loadingType}
            </span>
          )}
          {c?.avg_cycle_time_sec != null && (
            <span className="text-xs text-ink-soft dark:text-slate-400">
              {fmtCycleTime(c.avg_cycle_time_sec)} cycle
              {c.seat_capacity ? ` · ${c.seat_capacity} seats` : ''}
            </span>
          )}
          {c?.max_capacity_per_hour != null && (
            <span className="text-xs text-ink-soft dark:text-slate-400">
              · {fmtNum(c.max_capacity_per_hour)}/hr peak
            </span>
          )}
        </div>
      )}

      {/* KPI row */}
      <div className="mt-3 grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-2 dark:bg-slate-900/50">
        <div className="text-center">
          <div className="text-[10px] font-medium uppercase tracking-wider text-ink-soft dark:text-slate-400">Credits</div>
          <div className="mt-0.5 font-mono text-sm font-bold text-ink dark:text-slate-100">{fmtNum(a.credits_collected)}</div>
        </div>
        <div className="text-center">
          <div className="text-[10px] font-medium uppercase tracking-wider text-ink-soft dark:text-slate-400">Est. Earn</div>
          <div className="mt-0.5 font-mono text-sm font-bold text-butler-700 dark:text-butler-300">{fmtNum(a.estimated_earnings)}</div>
        </div>
        <div className="text-center">
          <div className="text-[10px] font-medium uppercase tracking-wider text-ink-soft dark:text-slate-400">Per Guest</div>
          <div className="mt-0.5 font-mono text-sm font-bold text-ink dark:text-slate-100">{a.est_earnings_per_guest != null ? fmtDollars(a.est_earnings_per_guest) : '—'}</div>
        </div>
      </div>

      {/* Bottom stats */}
      <div className="mt-3 flex items-center justify-between text-xs text-ink-soft dark:text-slate-400">
        <span>{fmtNum(a.total_scans)} scans · {fmtNum(a.unique_guests)} guests</span>
        <div className="flex items-center gap-3">
          <span>Repeat: {a.repeat_rate != null ? `${a.repeat_rate.toFixed(2)}x` : '—'}</span>
          {ulrPct != null && <span>ULR: {fmtPct(ulrPct)}</span>}
        </div>
      </div>
    </article>
  )
}

export function RideIntelligenceCardSkeleton() {
  return (
    <div className="animate-pulse rounded-card border border-hairline bg-white p-4 shadow-card-soft dark:border-slate-700 dark:bg-slate-800">
      <div className="h-5 w-2/3 rounded bg-slate-200 dark:bg-slate-700" />
      <div className="mt-1 h-3 w-1/4 rounded bg-slate-200 dark:bg-slate-700" />
      <div className="mt-3 grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-2 dark:bg-slate-900/50">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col items-center gap-1">
            <div className="h-2 w-10 rounded bg-slate-200 dark:bg-slate-700" />
            <div className="h-4 w-14 rounded bg-slate-200 dark:bg-slate-700" />
          </div>
        ))}
      </div>
      <div className="mt-3 h-3 w-full rounded bg-slate-200 dark:bg-slate-700" />
    </div>
  )
}
